import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ArrowLeft, ArrowLeftRight, Fingerprint, UserCheck } from 'lucide-react'
import { useState } from 'react'

import { tillListQueryOptions, tillQueryOptions, vaultsQueryOptions } from '@/api/teller-api'
import type { TillMovement } from '@/api/teller-types'
import { AuditTrailDrawer } from '@/components/audit-trail-drawer'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { CashMovementDialog } from '@/features/teller/cash-movement-dialog'
import { directionLabel, movementKindLabel, tillTypeLabel } from '@/features/teller/constants'
import { AssignFieldTillDialog } from '@/features/teller/field-till-actions'
import { TELLER_PATHS, useTellerNavigate, useTellerParams, useTellerRawSearch } from '@/features/teller/navigation'
import { TELLER_PERMISSIONS, useTellerPermissions } from '@/features/teller/permissions'
import { FieldTillNotice, PermissionNotice, TillFactsPanel } from '@/features/teller/teller-panels'
import { TellerLink } from '@/features/teller/teller-link'
import { usePostingDay } from '@/features/teller/use-posting-day'
import { formatBusinessDate, formatDateTime } from '@/lib/datetime'
import { useIsAtLeast } from '@/lib/use-viewport'

import { tillDetailSearchSchema } from './search'

/**
 * One drawer, and the day's movements behind its balance.
 *
 * The date picker is empty by default and stays empty: leaving `businessDate` off the request asks
 * the server for its own current day, which is the ledger's day rather than this machine's. A date
 * defaulted from the browser would silently show the wrong day at any branch still working in
 * yesterday, which is exactly the branch a supervisor is looking at this screen about.
 */

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const MOVEMENTS_PER_PAGE = 25

const helper = dataTableColumns<TillMovement>()

const columns: DataTableColumn<TillMovement>[] = helper.columns([
  helper.accessor('postedAtUtc', {
    header: 'Posted',
    enableSorting: false,
    cell: (context) => (
      <span className="text-xs">{formatDateTime(context.row.original.postedAtUtc)}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-44', card: 'lead' },
  }),
  helper.accessor('kind', {
    header: 'Movement',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{movementKindLabel(context.row.original.kind)}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('direction', {
    header: 'Direction',
    enableSorting: false,
    cell: (context) => (
      <StatusBadge
        status={directionLabel(context.row.original.direction)}
        tone={context.row.original.direction === 'In' ? 'success' : 'neutral'}
      />
    ),
    meta: { widthClass: 'w-24' },
  }),
  helper.accessor('amountMinor', {
    header: 'Amount',
    enableSorting: false,
    cell: (context) => {
      const movement = context.row.original

      return (
        <MoneyDisplay
          // The wire carries a magnitude and a separate `direction`; the sign lives in the latter.
          // Rendering `amountMinor` with `signed` alone would put a `+` in front of every banking
          // and every withdrawal, so the direction is folded in here — a column headed Amount with
          // a leading `+` on cash that left the drawer is worse than no sign at all.
          value={movement.direction === 'Out' ? -movement.amountMinor : movement.amountMinor}
          currency={movement.currencyCode}
          signed
          colourNegative
          emphasis="strong"
        />
      )
    },
    meta: { align: 'right', widthClass: 'w-36', card: 'lead' },
  }),
  helper.accessor('balanceAfterMinor', {
    header: 'Balance after',
    enableSorting: false,
    cell: (context) => (
      <MoneyDisplay
        value={context.row.original.balanceAfterMinor}
        currency={context.row.original.currencyCode}
      />
    ),
    meta: { align: 'right', widthClass: 'w-40' },
  }),
  helper.accessor('purpose', {
    header: 'Purpose',
    enableSorting: false,
    cell: (context) => <span className="text-xs">{context.row.original.purpose}</span>,
  }),
  helper.accessor('journalEntryNumber', {
    header: 'Journal',
    enableSorting: false,
    // Null where the movement posted no journal entry of its own — shown as a dash rather than
    // as an empty string, because "no entry" and "an entry we did not fetch" are different facts.
    cell: (context) =>
      context.row.original.journalEntryNumber === null ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="font-mono text-2xs">{context.row.original.journalEntryNumber}</span>
      ),
    meta: { widthClass: 'w-36' },
  }),
  helper.accessor('postedBy', {
    header: 'By',
    enableSorting: false,
    cell: (context) => <span className="text-xs">{context.row.original.postedBy}</span>,
    meta: { widthClass: 'w-32' },
  }),
])

export function TillDetailPage() {
  const params = useTellerParams()
  const rawSearch = useTellerRawSearch()
  const navigate = useTellerNavigate()
  const permissions = useTellerPermissions()
  const day = usePostingDay()
  const isWide = useIsAtLeast('lg')

  const tillId = params.tillId ?? ''
  const search = tillDetailSearchSchema.parse(rawSearch)

  const [assigning, setAssigning] = useState(false)
  const [moving, setMoving] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)

  const query = useQuery({
    ...tillQueryOptions(tillId, search.businessDate ?? null),
    enabled: permissions.canViewTills && tillId !== '',
  })

  const vaults = useQuery({
    ...vaultsQueryOptions(day.branchId),
    enabled: permissions.canViewVaults && permissions.canMoveVaultCash && Boolean(day.branchId),
  })

  const tills = useQuery({
    ...tillListQueryOptions({ branchId: day.branchId ?? undefined, page: 1, pageSize: 100 }),
    enabled: permissions.canViewTills && permissions.canMoveVaultCash,
  })

  if (!permissions.canViewTills) {
    return (
      <>
        <PageHeader title="Till" description="A drawer and the movements behind its balance." />
        <PermissionNotice permission={TELLER_PERMISSIONS.tillView} what="This drawer" />
        <Alert tone="info">
          <div className="space-y-1">
            <AlertTitle>This includes the officer carrying it</AlertTitle>
            <AlertDescription>
              No shipped field role holds{' '}
              <span className="font-mono text-xs">{TELLER_PERMISSIONS.tillView}</span>, so a loan
              officer holding a field drawer cannot read its balance, its limit, or its headroom here.
              That is the permission catalogue as it stands, not a fault of this screen.
            </AlertDescription>
          </div>
        </Alert>
      </>
    )
  }

  const movements = query.data?.movements ?? []
  const pagedMovements = movements.slice(
    pageIndex * MOVEMENTS_PER_PAGE,
    (pageIndex + 1) * MOVEMENTS_PER_PAGE,
  )

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex, pageSize: MOVEMENTS_PER_PAGE }
    const next = typeof updater === 'function' ? updater(current) : updater
    setPageIndex(next.pageIndex)
  }

  return (
    <>
      <TellerLink
        to={TELLER_PATHS.cashPosition}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Cash position
      </TellerLink>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorSurface
          error={query.error}
          onRetry={() => void query.refetch()}
          title="This drawer could not be opened"
        />
      ) : (
        <>
          <PageHeader
            title={query.data.till.name}
            reference={query.data.till.tillNumber}
            description={`${tillTypeLabel(query.data.till.tillType)} at branch ${query.data.till.branchId}.`}
            badges={<StatusBadge status={query.data.till.status} />}
            actions={
              <>
                {permissions.canAssignFieldTill && query.data.till.tillType === 'Field' ? (
                  <Button variant="secondary" onClick={() => setAssigning(true)}>
                    <UserCheck />
                    Assign to an officer
                  </Button>
                ) : null}
                {permissions.canMoveVaultCash ? (
                  <Button
                    variant="primary"
                    onClick={() => setMoving(true)}
                    disabled={!isWide || !day.branchId}
                    title={isWide ? undefined : 'Moving cash asks for a wider display.'}
                  >
                    <ArrowLeftRight />
                    Move cash
                  </Button>
                ) : null}
                <Button asChild variant="ghost">
                  <TellerLink to={TELLER_PATHS.sessions} search={{ tillId: query.data.till.id }}>
                    Shifts on this drawer
                  </TellerLink>
                </Button>
                <Button variant="ghost" onClick={() => setAuditOpen(true)}>
                  <Fingerprint />
                  Audit trail
                </Button>
              </>
            }
          />

          <TillFactsPanel till={query.data.till} />

          {query.data.till.tillType === 'Field' ? <FieldTillNotice /> : null}

          {query.data.till.openSessionId ? (
            <Alert tone="info">
              <div className="space-y-1">
                <AlertTitle>A shift is open on this drawer</AlertTitle>
                <AlertDescription>
                  <TellerLink
                    to={TELLER_PATHS.session(query.data.till.openSessionId)}
                    className="font-medium text-brand-text hover:text-brand-text-hover"
                  >
                    Open the shift
                  </TellerLink>{' '}
                  to see its counts and its expected closing cash. A drawer takes one session at a
                  time, and one person at a time.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="w-56 space-y-1.5">
                <Label htmlFor="movement-date">Business date</Label>
                <Input
                  id="movement-date"
                  type="date"
                  value={search.businessDate ?? ''}
                  onChange={(event) => {
                    setPageIndex(0)
                    navigate(TELLER_PATHS.till(tillId), {
                      search: { businessDate: event.target.value === '' ? undefined : event.target.value },
                      replace: true,
                    })
                  }}
                  aria-describedby="movement-date-help"
                />
                <p id="movement-date-help" className="text-xs text-muted">
                  {search.businessDate
                    ? `Showing ${formatBusinessDate(search.businessDate)}.`
                    : day.businessDate
                      ? `Empty means the ledger's current day, which at this branch is ${formatBusinessDate(day.businessDate)}.`
                      : 'Empty means the ledger’s current day. This branch has no day open, so there may be nothing to show.'}
                </p>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={pagedMovements}
              rowCount={movements.length}
              pagination={{ pageIndex, pageSize: MOVEMENTS_PER_PAGE }}
              onPaginationChange={onPaginationChange}
              sorting={UNSORTED}
              onSortingChange={ignoreSortingChange}
              isFetching={query.isFetching}
              getRowId={(row) => row.id}
              pageSizeOptions={[MOVEMENTS_PER_PAGE]}
              emptyTitle="No movements"
              emptyDescription="Nothing has moved through this drawer on that business date."
            />
          </div>

          <AssignFieldTillDialog
            till={query.data.till}
            branchId={day.branchId}
            open={assigning}
            onOpenChange={setAssigning}
          />

          {day.branchId ? (
            <CashMovementDialog
              branchId={day.branchId}
              vaults={vaults.data ?? []}
              tills={tills.data?.items ?? []}
              open={moving}
              onOpenChange={setMoving}
              initialKind="TillToVault"
              initialTillId={query.data.till.id}
            />
          ) : null}

          {/*
            The trail for the drawer itself: creation, and every field-till assignment, both of
            which the server records under entityType `Till`. A cash count is *not* here — it is
            audited against its own `CashCount` entity — and a movement is recorded against
            `TillMovement`. The drawer itself is what this button is about.
          */}
          <AuditTrailDrawer
            open={auditOpen}
            onOpenChange={setAuditOpen}
            entityType="Till"
            entityId={query.data.till.id}
            subject={`${query.data.till.tillNumber} · ${query.data.till.name}`}
          />
        </>
      )}
    </>
  )
}
